import React, { forwardRef, useMemo } from 'react';
import {
  combineDateTimeLocal,
  formatDateTimeLocalPreview,
  getMinDateForInput,
  getMinTimeForDate,
  isDateTimeLocalInPast,
  isNativePickerIndicatorClick,
  openNativePicker,
  splitDateTimeLocalValue,
} from '../../utils/dateTimePickerUtils';

const inputClassName =
  'picker-native-input block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400';

export interface DateTimePickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  minDateTime?: Date;
  clearable?: boolean;
  dateLabel?: string;
  timeLabel?: string;
  timePlaceholder?: string;
  defaultTime?: string;
  clearLabel?: string;
  selectedPrefix?: string;
  pastMessage?: string;
}

export const DateTimePicker = forwardRef<HTMLInputElement, DateTimePickerProps>(({
  id,
  value,
  onChange,
  disabled = false,
  minDateTime,
  clearable = true,
  dateLabel = 'Data',
  timeLabel = 'Hora',
  timePlaceholder = 'Selecione a data primeiro',
  defaultTime = '18:00',
  clearLabel = 'Limpar prazo',
  selectedPrefix = 'Prazo selecionado',
  pastMessage = 'O prazo precisa ser uma data e hora futuras.',
}, ref) => {
  const { date, time } = splitDateTimeLocalValue(value);
  const minDate = getMinDateForInput(minDateTime);
  const minTime = getMinTimeForDate(date, minDateTime);
  const preview = formatDateTimeLocalPreview(value);
  const isPast = isDateTimeLocalInPast(value, minDateTime);

  const dateInputId = id ? `${id}-date` : undefined;
  const timeInputId = id ? `${id}-time` : undefined;

  const timeHelper = useMemo(() => {
    if (!date) {
      return timePlaceholder;
    }

    if (!time) {
      return `Sugestão: ${defaultTime} (horário comercial)`;
    }

    return '';
  }, [date, time, timePlaceholder, defaultTime]);

  const handleDateChange = (nextDate: string) => {
    if (!nextDate) {
      onChange('');
      return;
    }

    const nextTime = time || defaultTime;
    onChange(combineDateTimeLocal(nextDate, nextTime, defaultTime));
  };

  const handleTimeChange = (nextTime: string) => {
    if (!date) {
      return;
    }

    onChange(combineDateTimeLocal(date, nextTime, defaultTime));
  };

  const handleClear = () => {
    onChange('');
  };

  const handleNativePickerMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  const handleNativePickerClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (!isNativePickerIndicatorClick(e.currentTarget, e.clientX))
    {
      return;
    }

    openNativePicker(e.currentTarget);
  };

  return (
    <div className="space-y-1">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <label htmlFor={dateInputId} className="mb-1 block text-xs font-medium text-gray-600">
              {dateLabel}
            </label>
            <input
              ref={ref}
              id={dateInputId}
              type="date"
              className={inputClassName}
              value={date}
              min={minDate}
              onMouseDown={handleNativePickerMouseDown}
              onClick={handleNativePickerClick}
              onChange={(e) => handleDateChange(e.target.value)}
              disabled={disabled}
            />
          </div>

          <div>
            <label htmlFor={timeInputId} className="mb-1 block text-xs font-medium text-gray-600">
              {timeLabel}
            </label>
            <input
              id={timeInputId}
              type="time"
              className={inputClassName}
              value={time}
              min={minTime}
              onMouseDown={handleNativePickerMouseDown}
              onClick={handleNativePickerClick}
              onChange={(e) => handleTimeChange(e.target.value)}
              disabled={disabled || !date}
              placeholder={timePlaceholder}
            />
            {timeHelper && (
              <p className="mt-0.5 text-xs text-gray-500">{timeHelper}</p>
            )}
          </div>
        </div>

        {clearable && value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="shrink-0 self-end pb-2 text-xs text-indigo-600 hover:text-indigo-800 hover:underline sm:pb-2.5"
          >
            {clearLabel}
          </button>
        )}
      </div>

      {preview && (
        <p className={`text-xs ${isPast ? 'text-amber-700' : 'text-gray-500'}`}>
          {selectedPrefix}: {preview}
          {isPast && ` — ${pastMessage}`}
        </p>
      )}
    </div>
  );
});

DateTimePicker.displayName = 'DateTimePicker';
